<script>
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { usePointsStore } from '@/store/points';
import { useTransferPartnersStore } from '@/store/transferpartners';
import { useNextCardStore } from '@/store/nextcard.js';

export function useRewardsAndTransferPartners() {
    const loading = ref(true);
    const pointsStore = usePointsStore();
    const nextCardStore = useNextCardStore();
    const transferPartnersStore = useTransferPartnersStore();
    const { rewards } = storeToRefs(pointsStore);

    const initializePointsAndTransfersData = async () => {
        loading.value = true;
        try {
            await Promise.all([
                transferPartnersStore.getAllGroupTransferPartners(),
                pointsStore.getRewards(),
                await nextCardStore.getNextCards(),
            ]);
            transferPartnersStore.getCardRecs();
        } catch (error) {
            error.value = error;
            console.error(
                'Unable to initialize points and transfers data',
                error
            );
        } finally {
            loading.value = false;
        }
    };

    // points for programs that can be transferred to the property's program:
    const filterUserPoints = (propertyProgramId) => {
        const relevantPartners = transferPartnersStore.allTransferPartners
            .filter(
                (partnership) =>
                    partnership.destination_program_id === propertyProgramId
            )
            .reduce((accumulator, partner) => {
                // Check if any user has the program points for the partner's source program.
                const anyUserHasPoints = rewards.value.users.some((user) =>
                    user.programs.some(
                        (prog) =>
                            prog.id === partner.source_program_id &&
                            prog.points > 0
                    )
                );

                if (anyUserHasPoints) {
                    // If any user has pts, include all users, not just ones w pts.
                    const points = rewards.value.users.map((user) => {
                        const userProgram = user.programs.find(
                            (program) =>
                                program.id === partner.source_program_id
                        );

                        const usersTransferPoints = userProgram
                            ? userProgram.points
                            : 0;

                        return {
                            userId: user.id,
                            userName: user.name,
                            points: usersTransferPoints,
                        };
                    });

                    if (points.length > 0) {
                        accumulator.push({
                            programName: partner.source_program_name,
                            image: partner.source_program.image,
                            transferRateFrom: Number(
                                partner.transfer_rate_from
                            ),
                            transferRateTo: Number(partner.transfer_rate_to),
                            transferTime: partner.transfer_time,
                            transferInstructions: partner.transfer_instructions,
                            bonusExpiration: partner.bonus_expiration || null,
                            bonusRateFrom: Number(partner.bonus_rate_from),
                            bonusRateTo: Number(partner.bonus_rate_to),
                            points,
                        });
                    }
                }
                return accumulator;
            }, []);
        return relevantPartners;
    };

    // takes in pointsForProgram (user's pts for property's program), programArray (user's points that can be transferred, by program)
    const addEnoughPointsValues = (
        pointsCost,
        pointsForProgram,
        programArray
    ) => {
        return programArray.map((program) => ({
            ...program,
            points: program.points.map((user) => {
                const usersProgramPoints = pointsForProgram.find(
                    (u) => u.userId === user.userId
                ) || { points: 0 };

                const hasEnoughProgramPoints =
                    usersProgramPoints.points >= pointsCost;
                const hasEnoughCombinedPoints =
                    usersProgramPoints.points + user.points >= pointsCost;
                const hasEnoughTransferPoints = user.points >= pointsCost;

                return {
                    ...user,
                    hasEnoughCombinedPoints,
                    hasEnoughProgramPoints,
                    hasEnoughTransferPoints,
                };
            }),
        }));
    };

    return {
        addEnoughPointsValues,
        filterUserPoints,
        initializePointsAndTransfersData,
        loading,
    };
}
</script>
